import { useRef } from 'react';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import type { IconType } from 'react-icons';

import {
  animateTealIconHighlight,
  createStepHighlightCycle,
  killStepHighlightTweens,
  SBR_STEP_HIGHLIGHT_DURATION,
  SBR_TEAL_ICON_ON_DARK,
} from '../../lib/gsapHighlight';
import SbrIcon from './SbrIcon';

export type HomeHeroFeatureItem = {
  title: string;
  body: string;
  Icon: IconType;
};

type Props = {
  items: HomeHeroFeatureItem[];
  className?: string;
};

const CARD_IDLE_BORDER = 'rgba(147, 197, 253, 0.22)';

/** Homepage hero feature stack — entrance stagger + tile-to-tile highlight cycle. */
export default function HomeHeroFeatureCards({ items, className = '' }: Props) {
  const rootRef = useRef<HTMLDivElement | null>(null);

  useGSAP(
    () => {
      if (!rootRef.current) return;

      const cards = rootRef.current.querySelectorAll<HTMLElement>('.home-hero__feature-card');
      const icons = rootRef.current.querySelectorAll<HTMLElement>('.home-hero__feature-icon');
      if (!cards.length) return;

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      let cycleTween: gsap.core.Timeline | null = null;

      const highlightTile = (index: number) => {
        killStepHighlightTweens(icons);

        cards.forEach((card, j) => {
          const icon = icons[j];
          const isActive = j === index;

          if (icon) {
            animateTealIconHighlight(icon, isActive, SBR_TEAL_ICON_ON_DARK, 1.12);
          }

          if (isActive) {
            gsap.to(card, {
              y: -4,
              borderColor: 'rgba(0, 194, 171, 0.5)',
              boxShadow: '0 12px 28px rgba(0, 0, 0, 0.28)',
              duration: SBR_STEP_HIGHLIGHT_DURATION,
              ease: 'power2.out',
              overwrite: 'auto',
            });
          } else {
            gsap.set(card, {
              y: 0,
              borderColor: CARD_IDLE_BORDER,
              boxShadow: '0 0 0 transparent',
            });
          }
        });
      };

      const startCycle = () => {
        cycleTween?.kill();
        if (reducedMotion) {
          highlightTile(0);
          return;
        }
        cycleTween = createStepHighlightCycle(items.length, highlightTile);
      };

      const cleanup = () => {
        cycleTween?.kill();
        cycleTween = null;
      };

      if (reducedMotion) {
        gsap.set(cards, { opacity: 1, y: 0, clearProps: 'opacity,transform' });
        startCycle();
        return cleanup;
      }

      gsap.set(cards, { opacity: 0, y: 22 });

      const entrance = gsap.timeline({
        delay: 0.35,
        onComplete: startCycle,
      });

      cards.forEach((card, i) => {
        const offset = i * 0.16;
        entrance.to(card, { opacity: 1, y: 0, duration: 0.55, ease: 'power2.out' }, offset);

        const icon = icons[i];
        if (icon) {
          entrance.fromTo(
            icon,
            { scale: 0.8, opacity: 0.5 },
            { scale: 1, opacity: 1, duration: 0.5, ease: 'back.out(1.4)' },
            offset + 0.05,
          );
        }
      });

      return cleanup;
    },
    { scope: rootRef, dependencies: [items.length] },
  );

  return (
    <div
      ref={rootRef}
      className={`home-hero__feature-cards${className ? ` ${className}` : ''}`.trim()}
    >
      {items.map((item) => (
        <div key={item.title} className="home-hero__feature-card">
          <div className="home-hero__feature-icon" aria-hidden>
            <SbrIcon icon={item.Icon} size={22} />
          </div>
          <div className="home-hero__feature-title">{item.title}</div>
          <p className="home-hero__feature-body">{item.body}</p>
        </div>
      ))}
    </div>
  );
}
